// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE COMPETITOR WALL — ten full homepages side by side.
//
// Erez, 2026-07-29: "I want to see 10 homepages next to each other, the full
// homepages", built the way the Marketing board does it.
//
// WHO IS ON THE WALL, and why it is not the obvious list. For Israel I did NOT
// pick the global majors that happen to have a Tel Aviv office (Cushman's
// Inter Israel, Avison Young). Those are the same brand Dan sees in the US
// column. I picked the firms he actually loses a client to on a Tuesday:
// Tel Aviv office-brokerage shops. For the US I took the majors, because that
// is the benchmark for what "serious" looks like in this industry.
//
// So the wall reads as a real contrast: what his competitors ship, against
// what the top of the world ships. Falcon has no website at all today — that
// was requirement 4 from discovery.
//
// TECHNIQUE (skill://board, marketing version wall): real headless-Chromium
// full-page screenshots, NOT live iframes — a cross-origin iframe renders
// BLANK inside React Flow's transformed canvas. Each page was scrolled to the
// bottom before capture so lazy-loaded imagery is present, cookie banners were
// hidden with CSS rather than clicked, then right-sized to 900px wide so the
// tallest page (5,850px) stays far under the ~16384px GPU texture limit.
// Static ?v= cache tag, never Date.now() — a timestamp re-downloads every MB
// on every board open.
//
// Owner: Otto. Re-capture: agents/otto/site-wall/capture-sites.mjs
// ─────────────────────────────────────────────────────────────────────────────

const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/board/falcon/sites";
const V = "v1"; // bump when re-captured

const C = {
  ink: "#1B211E", ink2: "#4A534D", faint: "#8C948E",
  card: "#FFFFFF", hair: "#E3DED4", bone: "#F2EDE4",
  blue: "#2F6BB0", petrol: "#2C7E9E",
};
const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type Site = { key: string; name: string; url: string; note: string; flag: "IL" | "US" };

// ONE row, all ten (Erez, 2026-07-29). They were split into an Israel row and a
// USA row, which quietly asked you to compare within a country. In a single row
// the comparison you actually want — his competitors against the world's best —
// happens by scrolling straight across, and the country badge on each card
// carries the grouping instead of a heading.
const SITES: Site[] = [
  { flag: "IL", key: "il-1-tagassets",  name: "תג נכסים",        url: "tagassets.co.il",   note: "Inventory-first. The database IS the pitch — office listings front and centre, updated daily." },
  { flag: "IL", key: "il-2-nadlanteam", name: "נדלנטים",          url: "nadlanteam.co.il",  note: "Brokerage-first. Sells the service and the people, not a searchable stock." },
  { flag: "IL", key: "il-3-zuz",        name: "ZUZ יועצי נדל״ן",  url: "zuznadlan.co.il",   note: "Consultant positioning — 'advisors', not agents. Closest to Dan's own 'let me lead the process'." },
  { flag: "IL", key: "il-4-nextplace",  name: "נקסט פלייס",       url: "nextplace.co.il",   note: "Niche: startups and tech tenants since 2016. A narrower promise than the rest." },
  { flag: "IL", key: "il-5-hiland",     name: "Hiland",           url: "hiland.co.il",      note: "A marketplace, not a broker — thousands of listings. The nearest thing Israel has to a public CRE board." },
  { flag: "IL", key: "il-6-topland",    name: "טופ לנד",          url: "top-land.co.il",    note: "City-by-city listing pages. SEO-led: the site is built to be found, not to be browsed." },
  { flag: "IL", key: "il-7-dh",         name: "DH יועצי נדל״ן",   url: "dh-nadlan.co.il",   note: "Another 'advisors' framing. The category Dan sits in is more crowded than it first looked." },
  { flag: "IL", key: "il-8-natam",      name: "נטעם",             url: "natam.co.il",       note: "Long-established commercial house. Institutional tone — closer to the majors than to a boutique." },
  { flag: "IL", key: "il-9-randb",      name: "R&B",              url: "randb.co.il",       note: "Location-led navigation: you pick a neighbourhood first, then a space. A tenant's mental model." },
  { flag: "IL", key: "il-10-avisonyoung", name: "Avison Young IL", url: "avisonyoung.co.il", note: "A global brand's Hebrew face. Compare it to its own international site — the gap is the localisation budget." },
  { flag: "IL", key: "il-11-madlan",    name: "מדלן מסחרי",       url: "madlan.co.il/commercial", note: "Data-first marketplace: prices, trends, maps. The closest thing Israel has to CoStar, and it is consumer-grade." },
  { flag: "IL", key: "il-12-commercial", name: "לוח נדל״ן מסחרי", url: "commercial.co.il",  note: "A pure commercial listings board. No brokerage, no advice — inventory as a product." },
  { flag: "IL", key: "il-13-remax",     name: "RE/MAX Israel",    url: "remax-israel.com",  note: "Franchise scale: 110+ offices, 1,300 agents. The volume play, and the opposite of Dan's concierge pitch." },
  { flag: "IL", key: "il-14-cbreil",    name: "CBRE Israel",      url: "cbre.co.il",        note: "The world's largest, localised. Worth reading directly against cbre.com further along this row." },
  { flag: "IL", key: "il-15-interisrael", name: "Cushman · Inter Israel", url: "inter-israel.com", note: "Cushman's Israeli arm, 45+ years. The most credentialled competitor on this wall." },
  { flag: "US", key: "us-1-cbre",       name: "CBRE",                url: "cbre.com",             note: "The largest in the world. Research and data lead; the brokerage is almost implied." },
  { flag: "US", key: "us-2-jll",        name: "JLL",                 url: "jll.com",              note: "Technology-forward positioning — the platform is the product." },
  { flag: "US", key: "us-3-cushman",    name: "Cushman & Wakefield", url: "cushmanwakefield.com", note: "Service-line breadth. Also runs Inter Israel in Tel Aviv, so Dan meets this brand locally." },
  { flag: "US", key: "us-4-colliers",   name: "Colliers",            url: "colliers.com",         note: "Entrepreneurial framing — closest of the majors to how an independent broker talks." },
  { flag: "US", key: "us-5-marcus",     name: "Marcus & Millichap",  url: "marcusmillichap.com",  note: "Investment-sales specialist. The most transactional of the five." },
];

function SiteFrame({ s, width = 900 }: { s: Site; width?: number }) {
  return (
    <div data-zoom style={{
      width, boxSizing: "border-box", background: C.card, border: `1px solid ${C.hair}`,
      borderRadius: 14, boxShadow: "0 10px 30px rgba(27,33,30,0.06)", overflow: "hidden",
      fontFamily: FONT, display: "flex", flexDirection: "column",
    }}>
      {/* browser chrome, so a stack of screenshots reads as a stack of pages */}
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 14px",
                    background: C.bone, borderBottom: `1px solid ${C.hair}`, flexShrink: 0 }}>
        <span style={{ display: "flex", gap: 5 }}>
          {["#E5675C", "#E7B44C", "#5FB07A"].map((c) => (
            <span key={c} style={{ width: 9, height: 9, borderRadius: 999, background: c }} />
          ))}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 11.5, color: C.ink2, whiteSpace: "nowrap",
                       overflow: "hidden", textOverflow: "ellipsis" }}>{s.url}</span>
        <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 600, color: C.ink,
                       whiteSpace: "nowrap" }}>{s.name}</span>
        {/* the country badge carries the grouping now that the two rows are one */}
        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: "0.1em",
                       color: "#fff", background: s.flag === "IL" ? C.petrol : C.blue,
                       borderRadius: 999, padding: "3px 8px", flexShrink: 0 }}>{s.flag}</span>
      </div>
      {/* the page. No loading="lazy" — an unloaded img is 0px tall inside the
          zoom canvas and collapses the whole column. */}
      <img
        src={`${R2}/${s.key}.jpg?${V}`}
        alt={`${s.name} homepage`}
        decoding="async"
        style={{ display: "block", width: "100%", height: "auto" }}
      />
      <div style={{ padding: "13px 16px", borderTop: `1px solid ${C.hair}`,
                    fontSize: 13, lineHeight: 1.5, color: C.ink2, flexShrink: 0 }}>
        {s.note}
      </div>
    </div>
  );
}

export function SiteWall() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ fontFamily: FONT, fontSize: 14.5, lineHeight: 1.55, color: C.ink2, maxWidth: 1240 }}>
        Twenty full homepages, captured live and shown end to end — <b>fifteen Israeli, then the five American
        majors</b>. The Israeli set covers all three things Dan is measured against: the <b>boutique office-brokerage
        shops</b> he loses a client to, the <b>marketplaces</b> that hold the inventory, and the <b>global brands'
        Hebrew face</b> (CBRE Israel and Cushman's Inter Israel sit here and again further along in English — the gap
        between the two is the localisation budget). <b>Falcon has no website at all today</b>, which was requirement 4
        out of discovery.
        <br />
        <span style={{ color: C.faint, fontSize: 13.5 }}>
          Yad2 is missing on purpose. It is the most relevant comparison of all — Dan's own words were that he wants
          “my own Yad2” — but it served a bot-detection challenge, and working around that is not something we do.
          Screenshot it by hand if you want it on the wall.
        </span>
      </div>
      {/* ONE row, ten full pages, aligned to the top so they compare fold-for-
          fold. nowrap is load-bearing: without it the flex row would break into
          a column at the parent's width and the whole point is lost. */}
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "nowrap" }}>
        {SITES.map((s) => <SiteFrame key={s.key} s={s} />)}
      </div>
    </div>
  );
}
